import React, { useId, useState } from 'react';

import {
  Button,
  Popover,
  Counter,
} from '@usealloy/component-library';
import { formatPublishedAttributeValue } from '~/entities/published-attributes/utils';
import {
  AttributesContainer,
  AttributesHeader,
  AttributeItem,
  AttributeName,
  AttributeValue,
} from './PublishedAttributesCellStyles';
import type { PublishedAttribute } from './types';

interface PublishedAttributesCellWithPopoverProps {
  attributes: Array<PublishedAttribute>;
}

export const PublishedAttributesCell = ({
  attributes,
}: PublishedAttributesCellWithPopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const headingId = useId();

  if (!attributes || attributes.length === 0) {
    return <span>—</span>;
  }

  const popoverContent = (
    <AttributesContainer
      justifyContent="flex-start"
      alignItems="stretch"
      style={{ backgroundColor: 'var(--cl-background-default)' }}
    >
      <section aria-labelledby={headingId}>
        <AttributesHeader weight={700} size={1} id={headingId}>
          Published Attributes
        </AttributesHeader>
      </section>
      <ul>
        {attributes.map(attr => {
          const formattedValue = formatPublishedAttributeValue(attr.value, attr.data_type) ?? '';
          return (
            <AttributeItem key={attr.token || attr.name} data-testid="published-attribute-item">
              <AttributeName>{attr.name}:</AttributeName>
              <AttributeValue>{formattedValue}</AttributeValue>
            </AttributeItem>
          );
        })}
      </ul>
    </AttributesContainer>
  );

  return (
    <Popover
      align="end"
      side="bottom"
      isOpen={isPopoverOpen}
      onClickOutside={() => setIsPopoverOpen(false)}
      style={{
        width: '320px',
        maxHeight: '300px',
        backgroundColor: 'var(--cl-background-default)',
        boxShadow: '0px 2px 8px rgba(0,0,0, 0.2)',
        overflowY: 'scroll',
      }}
      trigger={(
        <Button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isPopoverOpen}
          aria-label={`View ${attributes.length} published attribute${attributes.length !== 1 ? 's' : ''}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={() => setIsPopoverOpen(prev => !prev)}
        >
          <Counter count={attributes.length} />
        </Button>
      )}
    >
      {popoverContent}
    </Popover>
  );
};
